import { FormTab } from '@/platform/editor/form-tab';
import { IFormOperations } from '@/platform/editor/i-form-operations';
import { IFormValidationError } from '@/platform/editor/i-form-validation-error';
import { FormInstance, FormRules } from 'element-plus';
import { ref, reactive, watch, WatchStopHandle, toRaw, Ref } from 'vue';

import ScholarshipFormRules from './scholarship-form-rules';
import ScholarshipFormModel from './scholarship-form-model';
import { ScholarshipRelatedData } from './scholarship-related-data';

import StatePresenter from '@/presenters/state-presenter';
import { arrayRemoveItems } from '@/platform/core/array-extensions';
import { EditorIntegration } from '@/infrastructure/editor-platform/editor-integration';

import { SelectableData, selectableDataFromItems } from './csv-input/selectable-data';
import { MassSelectFormField } from './mass-select/mass-select-form-field';

export default class ScholarshipForm implements IFormOperations {
	public rules = reactive<FormRules>(ScholarshipFormRules);
	public relatedData = reactive(new ScholarshipRelatedData());
	public formRef = ref<FormInstance>();
	public formModel = ref<ScholarshipFormModel>();

	public showDestinationStates = ref<boolean>(false);
	public showOriginStates = ref<boolean>(false);

	private watchStopHandleFormChange: WatchStopHandle | undefined;

	public constructor(formModel: ScholarshipFormModel | undefined) {
		this.formModel.value = formModel;
	}

	public async mounted(): Promise<void> {
		await ScholarshipRelatedData.build(this.relatedData);
		await this.prepareForm();
	}

	public async replace(formModel: ScholarshipFormModel | undefined): Promise<void> {
		this.formModel.value = formModel;
		this.formModelWatchesUninstall();

		await this.prepareForm();
	}

	private async prepareForm(): Promise<void> {
		this.formModelWatchesInstall();

		await this.guardFormConsistency();

		this.formRef.value?.clearValidate();
	}

	private formModelWatchesInstall(): void {
		this.watchStopHandleFormChange = watch(
			[
				(): Ref => this.formModel.value!.sourceUrl,
				(): Ref => this.formModel.value!.foundAtUrl,
				(): Ref => this.formModel.value!.destinationCountries.allSelected,
				(): Ref => this.formModel.value!.destinationCountries.specific.keys,
				(): Ref => this.formModel.value!.destinationStates.allSelected,
				(): Ref => this.formModel.value!.destinationStates.specific.keys,
				(): Ref => this.formModel.value!.originCountries.allSelected,
				(): Ref => this.formModel.value!.originCountries.specific.keys,
				(): Ref => this.formModel.value!.originStates.allSelected,
				(): Ref => this.formModel.value!.originStates.specific.keys,
				(): Ref => this.formModel.value!.disciplines.allSelected,
				(): Ref => this.formModel.value!.disciplines.specific.keys,
				(): Ref => this.formModel.value!.grantDescription,
				(): Ref => this.formModel.value!.grantAmount,
				(): Ref => this.formModel.value!.grantCurrencyIso.inputValue,
				(): Ref => this.formModel.value!.deadline.isAnytime,
				(): Ref => this.formModel.value!.deadline.isNotSpecified,
				(): Ref => this.formModel.value!.deadline.isSpecified,
				(): Ref => this.formModel.value!.deadline.date,
				(): Ref => this.formModel.value!.degrees,
				(): Ref => this.formModel.value!.title,
				(): Ref => this.formModel.value!.description.content,
				(): Ref => this.formModel.value!.eligibility.content,
				(): Ref => this.formModel.value!.benefits.content,
				(): Ref => this.formModel.value!.applicationInfo.content,
				(): Ref => this.formModel.value!.applicationFormUrl,
				(): Ref => this.formModel.value!.provider.providerOrganisationId,
				(): Ref => this.formModel.value!.provider.providerBodyId,
				(): Ref => this.formModel.value!.provider.provider,
				(): Ref => this.formModel.value!.minimumExperience.inputValue,
				(): Ref => this.formModel.value!.isAwardedMultipleTimes,
				(): Ref => this.formModel.value!.applicationBasis,
				(): Ref => this.formModel.value!.formats,
				(): Ref => this.formModel.value!.types,
				(): Ref => this.formModel.value!.ageMin,
				(): Ref => this.formModel.value!.ageMax,
				(): Ref => this.formModel.value!.gender.inputValue,
				(): Ref => this.formModel.value!.race.inputValue,
				(): Ref => this.formModel.value!.summary.content,
				(): Ref => this.formModel.value!.isPublished,
			],
			async (): Promise<void> => {
				EditorIntegration.instance.notifyFormDirty();

				await this.guardFormConsistency();
			}
		);
	}

	public onAllDestinationCountriesSelected(): void {
		const formModel = toRaw(this.formModel);

		if (formModel.value) {
			const destinationStates = toRaw(formModel.value.destinationStates);
			destinationStates.allSelected.value = true;
		}
	}

	public onAllOriginCountriesSelected(): void {
		const formModel = toRaw(this.formModel);

		if (formModel.value) {
			const originStates = toRaw(formModel.value.originStates);
			originStates.allSelected.value = true;
		}
	}

	private formModelWatchesUninstall(): void {
		if (this.watchStopHandleFormChange !== undefined) {
			this.watchStopHandleFormChange();
		}
	}

	private async guardFormConsistency(): Promise<void> {
		const formModel = toRaw(this.formModel.value);
		if (formModel === undefined) {
			return;
		}

		this.relatedData.destinationStates = await this.restrictStatesForCountries(
			formModel.destinationCountries,
			formModel.destinationStates.specific.keys.value ?? []
		);
		this.showDestinationStates.value = this.relatedData.destinationStates.length > 0;

		this.relatedData.originStates = await this.restrictStatesForCountries(
			formModel.originCountries,
			formModel.originStates.specific.keys.value ?? []
		);
		this.showOriginStates.value = this.relatedData.originStates.length > 0;

		if (formModel.ageMin.value && formModel.ageMax.value && formModel.ageMax.value < formModel.ageMin.value) {
			formModel.ageMax.value = formModel.ageMin.value;
		}
	}

	private async restrictStatesForCountries(
		countriesSelection: MassSelectFormField,
		stateIds: string[]
	): Promise<readonly SelectableData[]> {
		const statesAvailable = countriesSelection.allSelected.value
			? await StatePresenter.all()
			: await StatePresenter.forCountries(countriesSelection.specific.keys.value);
		const statesAvailableIds = new Set<string>(statesAvailable.map((s) => s.id));

		const statesUnavailable = stateIds.filter((stateId) => !statesAvailableIds.has(stateId));
		arrayRemoveItems(stateIds, statesUnavailable);

		return selectableDataFromItems(
			statesAvailable,
			(s) => s.id,
			(s) => s.name
		);
	}

	public async validateField(prop: string): Promise<boolean> {
		try {
			await this.formRef.value?.validateField(prop);
			return true;
		} catch (e) {
			return false;
		}
	}

	public async validate(): Promise<readonly IFormValidationError[]> {
		try {
			await this.formRef.value?.validate();
			return [];
		} catch (e) {
			return [{ tab: FormTab.BASIC_INFORMATION }];
		}
	}
}
